import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAuth } from "@/app/providers/useAuth";
import { routes } from "@/app/navigation/routes";
import { useAddress } from "@/shared/hooks/useAddress";
import { couponService, paymentSubscriptionService } from "@/features/protest/services";
import { handleProceedToPayment } from "@/features/protest/viewModal/stripe";
import { useReferralSettings } from "@/features/referrals/viewmodel";
import { toastService } from "@/shared/toast";
import { tokenService } from "@/services/auth/token.service";
import { propertyService } from "@/features/properties/model/propertyServices";

type PaymentIntentData = {
  client_secret: string;
  payment_intent_id: string;
};

/** Matches backend expectation for `property_id` on payment create-intent. */
const looksLikePropertyUuid = (value: string): boolean => {
  const v = value.trim();
  if (!v) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
    return true;
  }
  return /^[0-9a-f]{32}$/i.test(v);
};

/** Fallback while price-descriptions loads or if the request fails. */
const DEFAULT_BASE_PROTEST_AMOUNT = 49;
const ANNUAL_PROTEST_PRICE_NAME = "annual_protest";
const MIN_PAYABLE_AMOUNT = 1;
/** Sent to coupon apply API; set to null if your backend expects no category. */
const PROTEST_COUPON_CATEGORY: string | null = "protest";

export const useProtestPaymentHero = () => {
  const { user, isAuthHydrated } = useAuth();
  const { selectedAddress, isAddressHydrated } = useAddress();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { statistics } = useReferralSettings();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [useReferralPoints, setUseReferralPoints] = useState(false);
  const [paymentIntentData, setPaymentIntentData] = useState<PaymentIntentData | null>(null);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discountAmount: number;
    finalAmount?: number;
  } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [baseProtestAmount, setBaseProtestAmount] = useState(DEFAULT_BASE_PROTEST_AMOUNT);

  const availableReferralPoints = Number(statistics?.totalPointsUsed ?? 0);
  const subtotalAfterReferral = useReferralPoints
    ? Math.max(MIN_PAYABLE_AMOUNT, baseProtestAmount - availableReferralPoints)
    : baseProtestAmount;

  const payableAmount = (() => {
    if (!appliedCoupon) return subtotalAfterReferral;
    if (appliedCoupon.finalAmount != null) {
      return Math.max(MIN_PAYABLE_AMOUNT, appliedCoupon.finalAmount);
    }
    return Math.max(MIN_PAYABLE_AMOUNT, subtotalAfterReferral - appliedCoupon.discountAmount);
  })();

  const persistedUser = tokenService.getUser();
  const effectiveUserId = user?.id ?? persistedUser?.id;

  const hasValidUserId =
    effectiveUserId !== undefined &&
    effectiveUserId !== null &&
    String(effectiveUserId).trim() !== "";

  const canStartProtest = useMemo(
    () =>
      isAuthHydrated &&
      isAddressHydrated &&
      looksLikePropertyUuid(selectedAddress || "") &&
      hasValidUserId,
    [isAuthHydrated, isAddressHydrated, selectedAddress, hasValidUserId]
  );

  useEffect(() => {
    if (useReferralPoints) {
      setAppliedCoupon(null);
      setCouponError(null);
      setCouponCodeInput("");
    }
  }, [useReferralPoints]);

  useEffect(() => {
    let cancelled = false;

    const loadBaseProtestAmount = async () => {
      try {
        const descriptions = await propertyService.getPriceDescriptions();
        const annualProtest = descriptions.find(
          (item) => item.name === ANNUAL_PROTEST_PRICE_NAME && item.status === "active"
        );
        const amount = Number(annualProtest?.amount);
        if (!cancelled && Number.isFinite(amount) && amount > 0) {
          setBaseProtestAmount(amount);
        }
      } catch (error) {
        console.error("Failed to load protest price description:", error);
      }
    };

    void loadBaseProtestAmount();

    return () => {
      cancelled = true;
    };
  }, []);

  const onStartProtest = async () => {
    if (!isAuthHydrated) {
      toastService.show("User session still loading. Please wait a moment.", "error");
      return;
    }
    if (!isAddressHydrated) {
      toastService.show("Property session still loading. Please wait a moment.", "error");
      return;
    }
    const propertyId = (selectedAddress || "").trim();

    console.log("selectedAddress", selectedAddress);
    console.log("effectiveUserId", effectiveUserId);
    console.log("canStartProtest", canStartProtest);
    console.log("isAddressHydrated", isAddressHydrated);

    if (!looksLikePropertyUuid(propertyId)) {
      toastService.show("Please select a property before paying.", "error");
      return;
    }
    if (!hasValidUserId) {
      toastService.show("Session still loading. Please try again.", "error");
      return;
    }
    try {
      setIsProcessing(true);
      const intent = await handleProceedToPayment({
        amount: baseProtestAmount,
        useWallet: useReferralPoints,
        userId: String(effectiveUserId),
        propertyId,
        couponCode: appliedCoupon?.code ?? "",
      });
      setPaymentIntentData({
        client_secret: intent.client_secret,
        payment_intent_id: intent.payment_intent_id,
      });
      setIsPaymentModalOpen(true);
    } catch (error) {
      console.error("Payment intent error:", error);
      window.alert("Unable to start payment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const onConfirmCardPayment = async () => {
    try {
      if (!stripe || !elements) {
        window.alert("Payment form is still loading. Please try again.");
        return;
      }
      if (!paymentIntentData) {
        window.alert("Payment session missing. Please start again.");
        return;
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        window.alert("Please enter your card details.");
        return;
      }

      setIsProcessing(true);
      const result = await stripe.confirmCardPayment(paymentIntentData.client_secret, {
        payment_method: { card },
      });

      if (result.error) {
        throw new Error(result.error.message || "Payment failed");
      }

      if (result.paymentIntent?.status === "succeeded") {
        await paymentSubscriptionService.updatePaymentStatus({
          payment_intent_id: paymentIntentData.payment_intent_id,
        });
        setPaymentIntentData(null);
        setIsPaymentModalOpen(false);
        // window.alert("Payment successful!");
        toastService.show("Payment successful!", "success");
        navigate(routes.selectPropertyType);
      }
    } catch (error) {
      console.error("Payment confirm error:", error);
      // window.alert("Unable to complete payment. Please try again.");
      toastService.show("Unable to complete payment. Please try again.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const onApplyCoupon = async () => {
    if (useReferralPoints) {
      setCouponError("Turn off referral rewards to use a promo code.");
      return;
    }
    const code = couponCodeInput.trim();
    if (!code) {
      setCouponError("Enter a promo code.");
      return;
    }
    const userId = user?.id != null ? String(user.id) : "";
    if (!userId) {
      setCouponError("You must be signed in to apply a promo code.");
      return;
    }

    setCouponError(null);
    setIsApplyingCoupon(true);
    try {
      const breakdown = await couponService.applyCouponCode({
        code,
        user_id: userId,
        order_amount: subtotalAfterReferral,
        category: PROTEST_COUPON_CATEGORY,
      });
      if (breakdown.discountAmount <= 0 && breakdown.finalAmount == null) {
        throw new Error("This promo code did not change your order total.");
      }
      setUseReferralPoints(false);
      const finalFromApi = breakdown.finalAmount;
      setAppliedCoupon({
        code,
        discountAmount: breakdown.discountAmount,
        finalAmount: finalFromApi != null ? Math.max(MIN_PAYABLE_AMOUNT, finalFromApi) : undefined,
      });
    } catch (e) {
      setAppliedCoupon(null);
      setCouponError(e instanceof Error ? e.message : "Unable to apply promo code.");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const onRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
  };

  return {
    isProcessing,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    useReferralPoints,
    setUseReferralPoints,
    availableReferralPoints,
    payableAmount,
    canStartProtest,
    onStartProtest,
    onConfirmCardPayment,
    baseProtestAmount,
    minPayableAmount: MIN_PAYABLE_AMOUNT,
    couponCodeInput,
    setCouponCodeInput,
    appliedCoupon,
    couponError,
    isApplyingCoupon,
    onApplyCoupon,
    onRemoveCoupon,
    subtotalAfterReferral,
  };
};
